/**
 * Шар локалізації за [ADR-0013]. Мова поки одна, але структура з першого дня:
 * додати англійську потім — це новий файл, а не прохід по компонентах.
 *
 * Формат повідомлень — ICU. Причина конкретна: українська має три форми
 * множини («1 справа», «2 справи», «5 справ»), і жодна саморобна підстановка
 * цього не вирішує так, щоб рішення пережило другу мову.
 *
 * Правило: у JSX не буває рядкових літералів. Лінтер це ловить.
 */
import IntlMessageFormat from "intl-messageformat";
import uk from "./locales/uk.js";
// Лише тип: байти каталогу лишаються в тому шматку, який його імпортує.
import type ukAccount from "./locales/uk.account.js";
import type ukAppearance from "./locales/uk.appearance.js";
import type ukCompose from "./locales/uk.compose.js";
import type ukExport from "./locales/uk.export.js";
import type ukGoogle from "./locales/uk.google.js";
import type ukInstall from "./locales/uk.install.js";
import type ukLock from "./locales/uk.lock.js";
import type ukMore from "./locales/uk.more.js";
import type ukRecords from "./locales/uk.records.js";
import type ukSearch from "./locales/uk.search.js";
import type ukPeople from "./locales/uk.people.js";
import type ukPlan from "./locales/uk.plan.js";
import type ukProfile from "./locales/uk.profile.js";
import type ukSync from "./locales/uk.sync.js";
import type ukYear from "./locales/uk.year.js";

export const LOCALE = "uk";

const messages: Record<string, string> = { ...uk };

/**
 * Ключі каталогів, які реєструються ліниво, входять у тип нарівні з
 * головним. Інакше вибір «не тягнути рядки в холодний старт» коштував
 * би перевірки ключів компілятором — а вона тут головна: `t()` кидає
 * на відсутньому ключі лише в розробці.
 */
export type MessageKey =
  | keyof typeof uk
  | keyof typeof ukSync
  | keyof typeof ukAccount
  | keyof typeof ukAppearance
  | keyof typeof ukCompose
  | keyof typeof ukExport
  | keyof typeof ukGoogle
  | keyof typeof ukInstall
  | keyof typeof ukLock
  | keyof typeof ukMore
  | keyof typeof ukRecords
  | keyof typeof ukSearch
  | keyof typeof ukPeople
  | keyof typeof ukPlan
  | keyof typeof ukProfile
  | keyof typeof ukYear;

/**
 * Долучити каталог лінивого екрана. Кличеться на верхньому рівні модуля,
 * який його імпортує, тож на момент першого малювання ключі вже на місці.
 */
export function addMessages(more: Readonly<Record<string, string>>): void {
  Object.assign(messages, more);
}

export type Values = Record<string, string | number | Date>;

/**
 * Розбір ICU коштує помітно дорожче за саме форматування, тому розібране
 * повідомлення живе до кінця сесії. Ключ кешу містить мову: при перемиканні
 * мови старі формати не мають лишитися чинними.
 */
const cache = new Map<string, IntlMessageFormat>();

export function format(template: string, values?: Values, locale: string = LOCALE): string {
  const cacheKey = `${locale} ${template}`;
  let mf = cache.get(cacheKey);
  if (!mf) {
    mf = new IntlMessageFormat(template, locale);
    cache.set(cacheKey, mf);
  }
  const out = mf.format(values);
  // ICU повертає масив, якщо в повідомленні є розмітка. У нас її немає,
  // і якщо колись з'явиться — краще впасти тут, ніж показати "[object Object]".
  if (typeof out !== "string") {
    throw new Error(`повідомлення не є простим текстом: ${template}`);
  }
  return out;
}

export function t(key: MessageKey, values?: Values): string {
  const template = messages[key];
  if (template === undefined) {
    // Мовчазний відкат на ключ ховає пропущений переклад до релізу.
    if (import.meta.env.DEV) throw new Error(`немає перекладу: ${key}`);
    return key;
  }
  try {
    return format(template, values);
  } catch (cause) {
    // Найчастіша причина — забуте значення аргументу. У розробці це помилка,
    // у продакшені показати ключ усе одно краще, ніж білий екран.
    if (import.meta.env.DEV) {
      throw new Error(`не вдалося зібрати ${key}: ${String(cause)}`, { cause });
    }
    return key;
  }
}
